import { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/atoms/Alert';
import { Button } from '@/components/atoms/Buttons';
import { Card } from '@/components/atoms/Card';
import { Spinner } from '@/components/atoms/Spinner';
import Tooltip from '@/components/atoms/Tooltip';
import { PageLayout } from '@/components/Layout';
import EmptyState from '@/components/molecules/EmptyState';
import SomethingHappened from '@/components/SomethingHappened';
import {
    type AuditFilterChange,
    type AuditLogEntry,
    buildAuditFilterChips,
    countByRequestId,
    loadedRequestCount,
} from '@/domain/auditLog';

import AuditLogDetails from './AuditLogDetails';
import ActiveFilterChips from './filters/ActiveFilterChips';
import AllFiltersPanel from './filters/AllFiltersPanel';
import AuditLogToolbar from './filters/AuditLogToolbar';
import type { AuditFilterBindings } from './filters/filterBindings';
import AuditColumnsMenu from './table/AuditColumnsMenu';
import AuditLogTable from './table/AuditLogTable';
import type {
    AuditColumnId,
    AuditColumnVisibility,
    AuditHideableColumnId,
} from './table/auditLogColumns';

import styles from './AuditLogView.module.scss';

export interface AuditLogViewProps {
    entries: AuditLogEntry[];
    isLoading: boolean;
    error: unknown;
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    onLoadMore: () => void;
    /** Reloads the loaded pages, so entries written since they were read show up. */
    onRefresh: () => void;
    isRefreshing: boolean;
    filterBindings: AuditFilterBindings;
    /** A click on a value in the table, or a filter button or pivot in the dialog. */
    onFilter: (change: AuditFilterChange) => void;
    columnVisibility: AuditColumnVisibility;
    /** The visible columns, in table order. */
    columns: AuditColumnId[];
    onColumnsChange: (visible: AuditHideableColumnId[]) => void;
    onExport: () => void;
    isExporting: boolean;
    exportError: string | null;
    selected: AuditLogEntry | null;
    onSelect: (entry: AuditLogEntry | null) => void;
}

export default function AuditLogView(props: AuditLogViewProps) {
    const {
        entries,
        isLoading,
        error,
        hasNextPage,
        isFetchingNextPage,
        onLoadMore,
        onRefresh,
        isRefreshing,
        filterBindings,
        onFilter,
        columnVisibility,
        columns,
        onColumnsChange,
        onExport,
        isExporting,
        exportError,
        selected,
        onSelect,
    } = props;
    const { t } = useTranslation();
    // React's ids hold colons, which jsdom's selector engine cannot match on the panel.
    const panelId = `audit-filters${useId().replaceAll(':', '')}`;
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const { filters, labelSources, onChange } = filterBindings;
    const chips = useMemo(
        () => buildAuditFilterChips(filters, labelSources),
        [filters, labelSources]
    );
    const requestCounts = useMemo(() => countByRequestId(entries), [entries]);

    const hasError = Boolean(error);
    const isEmpty = !isLoading && !hasError && entries.length === 0;
    // There is no total: counting every match would cost a scan the page cannot afford.
    const shownKey = hasNextPage ? 'auditLog.table.shownMore' : 'auditLog.table.shown';

    return (
        <PageLayout
            title={t('auditLog.page.title')}
            actions={
                <>
                    <Button
                        variant="secondary"
                        onClick={onRefresh}
                        disabled={isLoading || isRefreshing}>
                        {isRefreshing ? t('auditLog.page.refreshing') : t('auditLog.page.refresh')}
                    </Button>
                    {/* Button drops a `title`, and its own tooltip is too narrow for this text
                        and opens beside it, past the top of the page; this one opens below. */}
                    <Tooltip
                        placement="bottom-end"
                        content={t('auditLog.page.exportHint')}
                        maxWidth={280}
                        layout="inline">
                        <Button variant="primary" onClick={onExport} disabled={isExporting}>
                            {isExporting ? t('auditLog.page.exporting') : t('auditLog.page.export')}
                        </Button>
                    </Tooltip>
                </>
            }>
            {exportError !== null && (
                <Alert tone="danger" className={styles.exportError}>
                    {exportError}
                </Alert>
            )}
            <Card>
                <AuditLogToolbar
                    bindings={filterBindings}
                    activeCount={chips.length}
                    panelId={panelId}
                    isPanelOpen={isPanelOpen}
                    onTogglePanel={() => setIsPanelOpen((open) => !open)}
                />
                <ActiveFilterChips filters={filters} chips={chips} onChange={onChange} />
                <AllFiltersPanel
                    id={panelId}
                    isOpen={isPanelOpen}
                    onClose={() => setIsPanelOpen(false)}
                    bindings={filterBindings}
                />
                {hasError && <SomethingHappened title={t('auditLog.page.error')} />}
                <div className={styles.strip}>
                    <span className={styles.count}>
                        {entries.length > 0 && t(shownKey, { count: entries.length })}
                    </span>
                    <AuditColumnsMenu visibility={columnVisibility} onChange={onColumnsChange} />
                </div>
                {isLoading ? (
                    <Spinner size="lg" />
                ) : (
                    entries.length > 0 && (
                        <AuditLogTable
                            entries={entries}
                            columns={columns}
                            onSelect={onSelect}
                            onFilter={onFilter}
                        />
                    )
                )}
                {isEmpty && (
                    <EmptyState
                        title={t('auditLog.page.empty')}
                        appearance="quiet"
                        size="spacious"
                    />
                )}
                {hasNextPage && (
                    <div className={styles.loadMore}>
                        <Button
                            variant="secondary"
                            onClick={onLoadMore}
                            disabled={isFetchingNextPage}>
                            {isFetchingNextPage && <Spinner className={styles.spinner} />}
                            {t('auditLog.page.loadMore')}
                        </Button>
                    </div>
                )}
            </Card>
            {selected !== null && (
                <AuditLogDetails
                    entry={selected}
                    requestCount={loadedRequestCount(requestCounts, selected)}
                    onFilter={onFilter}
                    onClose={() => onSelect(null)}
                />
            )}
        </PageLayout>
    );
}
